import { defineCollection } from "@rebasepro/cms-types";
import challengeScoresCollection from "./challenge-scores";
import challengeAttemptsCollection from "./challenge-attempts";
import type { CollectionConfig } from "@rebasepro/types";

// Solo challenges (songbuzz_solo_duel): a link with its own songs, the seed being its id, and its own board in Challenge
// scores. backend/functions/songbuzz.ts creates the table and writes it; admins can take down one with a bad name.
const challengesCollection = defineCollection({
    name: "Challenges",
    singularName: "Challenge",
    slug: "challenges",
    table: "songbuzz_solo_duel",
    description: "Solo challenges: a link (songbuzz.tv/solo?c=…) that plays the same songs for everyone who opens it.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operations: ["update", "delete"], roles: ["admin"] },
    ],
    properties: {
        id: { name: "Code", description: "What the link carries: songbuzz.tv/solo?c=<code>", type: "string", isId: "manual", admin: { readOnly: true } },
        byName: { name: "Sent by", type: "string" },
        // a short hash of the sender's look, so the challenge draws them as they were
        byLook: { name: "Sender's look code", type: "string", admin: { readOnly: true, hideFromCollection: true } },
        scores: {
            name: "Board", type: "relation",
            relation: { kind: "via", cardinality: "many", target: (): CollectionConfig => challengeScoresCollection, relationName: "scores", joinPath: [{ table: "songbuzz_solo_duel_score", on: { from: "id", to: "duel" } }] },
            admin: { readOnly: true },
        },
        // everyone who opened it and started, scored or not
        attempts: {
            name: "Started by", type: "relation",
            relation: { kind: "via", cardinality: "many", target: (): CollectionConfig => challengeAttemptsCollection, relationName: "attempts", joinPath: [{ table: "songbuzz_solo_duel_attempt", on: { from: "id", to: "duel" } }] },
            admin: { readOnly: true },
        },
        at: { name: "Created", type: "date", autoValue: "on_create", validation: { required: true }, admin: { readOnly: true } },
    },
    admin: {
        icon: "Trophy",
        group: "Challenges",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: ({ entity }) => entity.values.byName || `Challenge ${entity.id}`,
            // the link players were sent
            subtitle: async ({ entity, context }) => {
                const n = await context.client.data.collection("challenge_scores").count({ where: { duel: ["==", String(entity.id)] } }).catch(() => 0);
                return `${n} ${n === 1 ? "score" : "scores"} on the board · songbuzz.tv/solo?c=${entity.id}`;
            },
            date: "at",
        },
        form: {
            sections: [
                { key: "challenge", properties: ["byName", "id", "at", "scores", "attempts"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["byLook"] },
            ],
        },
    },
});

export default challengesCollection;
